import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';

import { cn } from '../../components/ui/utils';
import { ROUTES } from '../../shared/config/routes';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { PartnersSection } from '../admin/PartnersSection';

/** Партнёры — вкладка без своего маршрута: ?tab=partners на «Сотрудниках». */
const PARTNERS_TAB = 'partners';

/**
 * «Сотрудники и доступы» вместо отдельной админки со своим сайдбаром:
 * это такой же раздел приложения, и выходить из него «← Назад» не нужно.
 *
 * Партнёры (внешние люди со своими аккаунтами) раньше висели внизу списка
 * сотрудников — теперь своя вкладка (02.11.2026).
 */
export function TeamPage(): JSX.Element | null {
  const { isAdmin, isLoading } = useCurrentUser();
  const { pathname, search } = useLocation();

  if (isLoading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;

  const onIndex = pathname.replace(/\/$/, '') === ROUTES.TEAM;
  const partners = onIndex && new URLSearchParams(search).get('tab') === PARTNERS_TAB;
  const tabs = [
    { to: ROUTES.TEAM, label: 'Сотрудники', active: onIndex && !partners },
    { to: `${ROUTES.TEAM}/assignments`, label: 'Доступы к аккаунтам', active: pathname.startsWith(`${ROUTES.TEAM}/assignments`) },
    { to: `${ROUTES.TEAM}?tab=${PARTNERS_TAB}`, label: 'Партнёры', active: partners },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Как SectionTabs, но активность считается и по ?tab: NavLink смотрит
          только на путь, и «Сотрудники» с «Партнёрами» горели бы вместе. */}
      <div className="-mx-1 overflow-x-auto">
        <nav className="flex min-w-max items-center gap-1 border-b px-1">
          {tabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={tab.active ? 'page' : undefined}
              className={cn(
                'group relative inline-flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors',
                'after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:transition-colors',
                tab.active
                  ? 'text-foreground after:bg-primary'
                  : 'text-muted-foreground after:bg-transparent hover:text-foreground',
              )}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>

      {partners ? <PartnersSection /> : <Outlet />}
    </div>
  );
}
